import {
  Card,
  closeDialog,
  DataStatus,
  Dialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  isCurrency,
  openDialog,
  Option,
  RadioOption,
  Separator,
  Switch,
  Tooltip,
} from 'platform/components';
import {Align, Box, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {DeepPartial} from 'utility-types';

import {useEffect, useId, useState} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isArray, isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetTenantQuery} from '@dms/api/core';
import {
  BaseDirectSale,
  DeleteCustomerContractRequest,
  DiscountWorkRate,
  PostCustomerContractRequest,
  PutDirectSaleDiscountRequest,
  useDeleteDirectSaleCustomerContractMutation,
  useGetCustomerContractQuery,
  useGetDirectSaleDiscountQuery,
  usePutCustomerContractMutation,
  usePutDirectSaleDiscountMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  CustomerContractModal,
  handleApiError,
  usePermissions,
  useWorkTypeOptions,
} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId, useBoolean} from 'shared/web';

import {PRICING_FIELD_WIDTH} from './constants/PRICING_FIELD_WIDTH';
import {CustomerContractMode, DiscountsTabCustomerContract} from './DiscountsTabCustomerContract';
import {DiscountsTabCustomerContractCard} from './DiscountsTabCustomerContractCard';
import {DiscountsTabDeactivateContract} from './DiscountsTabDeactivateContract';
import {DiscountsTabSavingCustomDiscounts} from './DiscountsTabSavingCustomDiscounts';
import {DiscountsTabWorkRateListField} from './DiscountsTabWorkRateListField';
import {DiscountTabsChangeDiscountType} from './DiscountTabsChangeDiscountType';
import {getWorkDiscountDefaults} from './utils/getWorkDiscountDefaults';
import {hasWorkDiscount} from './utils/hasWorkDiscount';

type DiscountType = 'percentage' | 'workRate';

type DiscountPercentage = number;

type WorkDiscount = {
  discountType: DiscountType;
  discountValue: DiscountWorkRate[] | DiscountPercentage;
};

type MaterialDiscountType = 'discountToSalePrice' | 'markupToPurchasePrice';

type MaterialDiscount = {
  discountType: MaterialDiscountType;
  discountValue: number;
};

type CustomDiscountForm = {
  workDiscount: WorkDiscount;
  materialDiscount: MaterialDiscount;
};

const DEACTIVATE_CONTRACT_ID = 'deactivateContractId';
const SAVING_CUSTOM_DISCOUNT_ID = 'savingCustomDiscountId';
const CHANGE_DISCOUNT_TYPE_ID = 'changeDiscountTypeId';

interface DiscountsTabProps extends RequiredTestIdProps {
  directSale?: BaseDirectSale;
  isEditingDisabled: boolean;
}

const PERCENTAGE_BASE = 100;
// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export function DiscountsTab(props: DiscountsTabProps) {
  const [contractDetailId, setContractDetailId] = useState<string>();
  const [assignContractMode, setAssignContractMode] = useState<CustomerContractMode | null>(null);
  const workDiscountSwitchId = useId();
  const materialDiscountSwitchId = useId();

  const [directSaleEdit] = usePermissions({
    permissionKeys: ['directSaleEdit'],
  });

  const [
    isDiscountOnWorkOpen,
    setDiscountOnWorkOpen,
    setDiscountOnWorkClose,
    toggleDiscountOnWork,
  ] = useBoolean(false);
  const [
    isDiscountOnMaterialOpen,
    setDiscountOnMaterialOpen,
    setDiscountOnMaterialClose,
    toggleDiscountOnMaterial,
  ] = useBoolean(false);

  const {
    data: tenantData,
    isLoading: isTenantLoading,
    isError: isTenantError,
  } = useGetTenantQuery();
  const {workTypes} = useWorkTypeOptions();

  const workTypeOptions: Option[] =
    workTypes?.map((workType) => ({
      label: workType?.name as string,
      value: workType?.id as string,
      isDisabled: workType?.isActive as boolean,
    })) ?? [];

  const {
    data: directSaleDiscount,
    isLoading: isDirectSaleDiscountLoading,
    isError: isDirectSaleDiscountError,
  } = useGetDirectSaleDiscountQuery(
    {
      directSaleId: props.directSale?.id as string,
    },
    {skip: !props.directSale?.id}
  );

  const {
    data: customerContract,
    isLoading: isCustomerContractLoading,
    isError: isLoadingisCustomerContractError,
  } = useGetCustomerContractQuery(
    {
      directSaleId: props.directSale?.id as string,
    },
    {skip: !props.directSale?.id}
  );

  const currency = props.directSale?.totalPrice?.withVat?.currency;

  const [putDirectSaleDiscount] = usePutDirectSaleDiscountMutation();
  const [putCustomerContract, {isLoading: isPutCustomerContractLoading}] =
    usePutCustomerContractMutation();
  const [deleteCustomerContract] = useDeleteDirectSaleCustomerContractMutation();

  const isLoading = isDirectSaleDiscountLoading || isCustomerContractLoading || isTenantLoading;
  const isError = isDirectSaleDiscountError || isLoadingisCustomerContractError || isTenantError;

  const hasDiscountToSalePrice = isNotNil(
    directSaleDiscount?.materialDiscount?.discountToSalePrice
  );
  const hasMarkupToPurchasePrice = isNotNil(
    directSaleDiscount?.materialDiscount?.markupToPurchasePrice
  );

  useEffect(
    function openActiveDiscountSections() {
      if (isNil(directSaleDiscount)) {
        return;
      }

      if (hasWorkDiscount(directSaleDiscount.workDiscount)) {
        setDiscountOnWorkOpen();
      }

      if (hasDiscountToSalePrice || hasMarkupToPurchasePrice) {
        setDiscountOnMaterialOpen();
      }
    },
    [directSaleDiscount] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  const defaultValues: DeepPartial<CustomDiscountForm> = {
    workDiscount: getWorkDiscountDefaults(directSaleDiscount?.workDiscount),
    materialDiscount: {
      discountType: hasMarkupToPurchasePrice ? 'markupToPurchasePrice' : 'discountToSalePrice',
      discountValue: hasMarkupToPurchasePrice
        ? Number(directSaleDiscount?.materialDiscount?.markupToPurchasePrice) * PERCENTAGE_BASE
        : Number(directSaleDiscount?.materialDiscount?.discountToSalePrice) * PERCENTAGE_BASE,
    },
  };

  const onCustomDiscountsSubmit = (isCustomDiscountApplied: boolean, data: CustomDiscountForm) => {
    const workDiscounts: DiscountWorkRate[] | undefined = isArray(data.workDiscount.discountValue)
      ? (data.workDiscount.discountValue as DiscountWorkRate[])
          .filter((discountWorkRate) => isNotNilOrEmpty(discountWorkRate.workType))
          .map((discountWorkRate) => ({
            workType: discountWorkRate.workType,
            workRateWithoutVat: {
              amount: discountWorkRate.workRateWithoutVat.amount,
              currency: tenantData?.currency as string,
            },
          }))
      : undefined;

    const putDirectSaleDiscountRequest: PutDirectSaleDiscountRequest = {
      directSaleId: props.directSale?.id as string,
      body: {
        directSaleDiscount: {
          workDiscount: isDiscountOnWorkOpen
            ? {
                percentageDiscount:
                  data.workDiscount.discountType === 'percentage'
                    ? (data.workDiscount.discountValue as number) / PERCENTAGE_BASE
                    : undefined,
                discountedWorkRate:
                  data.workDiscount.discountType === 'workRate' ? workDiscounts : undefined,
              }
            : null,
          materialDiscount: isDiscountOnMaterialOpen
            ? {
                discountToSalePrice:
                  data.materialDiscount.discountType === 'discountToSalePrice'
                    ? data.materialDiscount.discountValue / PERCENTAGE_BASE
                    : undefined,
                markupToPurchasePrice:
                  data.materialDiscount.discountType === 'markupToPurchasePrice'
                    ? data.materialDiscount.discountValue / PERCENTAGE_BASE
                    : undefined,
              }
            : null,
        },
        isDiscountApply: isCustomDiscountApplied,
      },
    };

    return putDirectSaleDiscount(putDirectSaleDiscountRequest).unwrap().catch(handleApiError);
  };

  const handleFormSubmit: FormSubmitHandler<CustomDiscountForm> = (data, _, reset) =>
    new Promise<void>((resolve) => {
      const submit = (isCustomDiscountApplied: boolean) =>
        onCustomDiscountsSubmit(isCustomDiscountApplied, data)
          .then(() => closeDialog(SAVING_CUSTOM_DISCOUNT_ID))
          .then(() => reset(undefined, {keepValues: true}))
          .then(() => resolve());

      const discard = () => {
        closeDialog(SAVING_CUSTOM_DISCOUNT_ID);
        resolve();
      };

      openDialog(
        <DiscountsTabSavingCustomDiscounts
          onSavingCustomDiscounts={submit}
          onDiscard={discard}
          data-testid="savingCustomDiscount-modal"
        />,
        {
          id: SAVING_CUSTOM_DISCOUNT_ID,
          onCloseComplete: discard,
          title: i18n.t('general.actions.savingCustomDiscount'),
          size: 'small',
          'data-testid': suffixTestId('savingCustomDiscount-modal', props),
        }
      );
    });

  const assignContract = (customerContractId: string, isDiscountApply: boolean) => {
    const putCustomerContractRequest: PostCustomerContractRequest = {
      directSaleId: props.directSale?.id as string,
      body: {
        customerContractId,
        isDiscountApply,
      },
    };

    return putCustomerContract(putCustomerContractRequest)
      .unwrap()
      .then(() => setAssignContractMode(null))
      .catch(handleApiError);
  };

  const onHandleAssignContract = (customerContractId: string, isDiscountApply: boolean) => {
    if (!isDiscountOnWorkOpen && !isDiscountOnMaterialOpen) {
      assignContract(customerContractId, isDiscountApply);
      return;
    }
    const discard = () => {
      closeDialog(CHANGE_DISCOUNT_TYPE_ID);
      setAssignContractMode(null);
    };
    const accept = () => {
      assignContract(customerContractId, isDiscountApply).then(() => {
        setDiscountOnWorkClose();
        setDiscountOnMaterialClose();
        discard();
      });
    };

    openDialog(<DiscountTabsChangeDiscountType onDiscard={discard} onAcceptChange={accept} />, {
      id: CHANGE_DISCOUNT_TYPE_ID,
      title: i18n.t('entity.warehouse.actions.changeDiscountTypeDialogHeader'),
      size: 'small',
      'data-testid': suffixTestId('changeDiscountType-modal', props),
    });
  };

  const onDeactivateContract = (isDiscountRemove: boolean) => {
    const deleteCustomerContractRequest: DeleteCustomerContractRequest = {
      directSaleId: props.directSale?.id as string,
      body: {
        isDiscountRemove,
      },
    };

    return deleteCustomerContract(deleteCustomerContractRequest)
      .unwrap()
      .then(() => closeDialog(DEACTIVATE_CONTRACT_ID))
      .catch(handleApiError);
  };

  const handleAssignContract = () => {
    setAssignContractMode('assign');
  };

  const handleChangeContract = () => {
    setAssignContractMode('change');
  };
  const handleDeactivateContract = () => {
    openDialog(
      <DiscountsTabDeactivateContract
        onDeactivateContract={onDeactivateContract}
        deactivateContractDialogId={DEACTIVATE_CONTRACT_ID}
        data-testid="deactivateContract-modal"
      />,
      {
        id: DEACTIVATE_CONTRACT_ID,
        title: i18n.t('general.actions.deactivateContract'),
        size: 'small',
        'data-testid': suffixTestId('deactivateContract-modal', props),
      }
    );
  };

  const handleContractDetail = (customerContractId: string) => {
    setContractDetailId(customerContractId);
  };

  const handleToggleDiscountOnWork = () => {
    if (isNil(customerContract)) {
      toggleDiscountOnWork();
      return;
    }

    const discard = () => closeDialog(CHANGE_DISCOUNT_TYPE_ID);
    const accept = () => {
      onDeactivateContract(true).then(() => {
        toggleDiscountOnWork();
        discard();
      });
    };

    openDialog(<DiscountTabsChangeDiscountType onDiscard={discard} onAcceptChange={accept} />, {
      id: CHANGE_DISCOUNT_TYPE_ID,
      title: i18n.t('entity.warehouse.actions.changeDiscountTypeDialogHeader'),
      size: 'small',
      'data-testid': suffixTestId('changeDiscountType-modal', props),
    });
  };

  const handleToggleDiscountOnMaterial = () => {
    if (isNil(customerContract)) {
      toggleDiscountOnMaterial();
      return;
    }

    const discard = () => closeDialog(CHANGE_DISCOUNT_TYPE_ID);
    const accept = () => {
      onDeactivateContract(true).then(() => {
        toggleDiscountOnMaterial();
        discard();
      });
    };

    openDialog(<DiscountTabsChangeDiscountType onDiscard={discard} onAcceptChange={accept} />, {
      id: CHANGE_DISCOUNT_TYPE_ID,
      title: i18n.t('entity.warehouse.actions.changeDiscountTypeDialogHeader'),
      size: 'small',
      'data-testid': suffixTestId('changeDiscountType-modal', props),
    });
  };

  const workDiscountOptions: RadioOption[] = [
    {
      value: 'percentage',
      label: i18n.t('entity.warehouse.labels.percentageDiscount'),
      isDisabled: props.isEditingDisabled,
    },
    {
      value: 'workRate',
      label: i18n.t('entity.warehouse.labels.discountWorkRate'),
      isDisabled: props.isEditingDisabled,
    },
  ];

  const materialDiscountOptions: RadioOption[] = [
    {
      value: 'discountToSalePrice',
      label: i18n.t('entity.warehouse.labels.discountOnSalePrice'),
      isDisabled: props.isEditingDisabled,
    },
    {
      value: 'markupToPurchasePrice',
      label: i18n.t('entity.warehouse.labels.markUpToPurchasePrice'),
      isDisabled: props.isEditingDisabled,
    },
  ];

  const isCustomDiscountDisabled =
    isNilOrEmpty(directSaleDiscount) && !isDiscountOnWorkOpen && !isDiscountOnMaterialOpen;

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Show when={isNotNil(contractDetailId)}>
        <CustomerContractModal
          customerContractId={contractDetailId ?? ''}
          onClose={() => setContractDetailId(undefined)}
          data-testid={testIds.customer.detail('contractDetailModal')}
        />
      </Show>
      <Dialog
        isOpen={isNotNil(assignContractMode)}
        onClose={() => setAssignContractMode(null)}
        title={i18n.t('general.actions.assignContract')}
        data-testid={suffixTestId('changeContract-modal', props)}
      >
        <Show when={isNotNil(assignContractMode)}>
          <DiscountsTabCustomerContract
            onHandleAssignContract={onHandleAssignContract}
            isPutCustomerContractLoading={isPutCustomerContractLoading}
            mode={assignContractMode as CustomerContractMode}
            onCloseDialog={() => setAssignContractMode(null)}
            data-testid="changeContract-modal"
            onContractSelect={handleContractDetail}
            directSale={props.directSale}
          />
        </Show>
      </Dialog>
      <VStack spacing={4}>
        <Show when={props.directSale?.customerId}>
          <DiscountsTabCustomerContractCard
            customerContract={customerContract}
            isEditingDisabled={props.isEditingDisabled}
            canEditDirectSale={directSaleEdit}
            onAssign={handleAssignContract}
            onView={() => setContractDetailId(customerContract?.id ?? undefined)}
            onChange={handleChangeContract}
            onDeactivate={handleDeactivateContract}
            data-testid={props['data-testid']}
          />
        </Show>
        <Form<CustomDiscountForm>
          defaultValues={defaultValues}
          onSubmit={handleFormSubmit}
          shouldWatchForUnsavedChanges
        >
          {(control, formApi) => {
            const workDiscountType = formApi.watch('workDiscount.discountType');

            return (
              <VStack spacing={4}>
                <Card variant="inlineGrey">
                  <VStack spacing={4}>
                    <VStack spacing={1}>
                      <Heading size={4}>
                        {i18n.t('entity.warehouse.labels.customDiscounts')}
                      </Heading>
                      <Text size="small" color="secondary">
                        {i18n.t('entity.warehouse.labels.customDiscountsDescription')}
                      </Text>
                    </VStack>
                    <Separator spacing={0} />
                    <HStack align="center" spacing={3}>
                      <Tooltip
                        isDisabled={directSaleEdit}
                        label={i18n.t('module.warehouse.label.missingDirectSaleEditPermission')}
                      >
                        <Switch
                          externalLabelId={workDiscountSwitchId}
                          value={isDiscountOnWorkOpen}
                          onChange={handleToggleDiscountOnWork}
                          isDisabled={props.isEditingDisabled || !directSaleEdit}
                          data-testid={suffixTestId('workDiscount.switch', props)}
                        />
                      </Tooltip>
                      <label htmlFor={workDiscountSwitchId}>
                        <Heading size={4}>
                          {i18n.t('entity.warehouse.labels.discountOnWork')}
                        </Heading>
                      </label>
                    </HStack>
                    <Show when={isDiscountOnWorkOpen}>
                      <VStack spacing={4}>
                        <FormField
                          control={control}
                          name="workDiscount.discountType"
                          type="radio"
                          options={workDiscountOptions}
                          data-testid={suffixTestId('workDiscount.discountType', props)}
                        />
                        <Show when={workDiscountType === 'percentage'}>
                          <Box width={PRICING_FIELD_WIDTH} maxWidth="100%">
                            <FormField
                              control={control}
                              name="workDiscount.discountValue"
                              type="number"
                              label={i18n.t('entity.warehouse.labels.percentage')}
                              suffix="%"
                              isDisabled={props.isEditingDisabled || !directSaleEdit}
                              tooltip={
                                !directSaleEdit
                                  ? i18n.t('module.warehouse.label.missingDirectSaleEditPermission')
                                  : undefined
                              }
                              data-testid={suffixTestId('workDiscount.discountValue', props)}
                            />
                          </Box>
                        </Show>
                        <Show when={workDiscountType === 'workRate'}>
                          <DiscountsTabWorkRateListField<CustomDiscountForm>
                            control={control}
                            formApi={formApi}
                            name="workDiscount.discountValue"
                            categoryOptions={workTypeOptions}
                            isDisabled={props.isEditingDisabled || !directSaleEdit}
                            currency={isCurrency(currency) ? currency : undefined}
                          />
                        </Show>
                      </VStack>
                    </Show>
                    <Separator spacing={0} />
                    <HStack align="center" spacing={3}>
                      <Tooltip
                        isDisabled={directSaleEdit}
                        label={i18n.t('module.warehouse.label.missingDirectSaleEditPermission')}
                      >
                        <Switch
                          externalLabelId={materialDiscountSwitchId}
                          value={isDiscountOnMaterialOpen}
                          onChange={handleToggleDiscountOnMaterial}
                          isDisabled={props.isEditingDisabled || !directSaleEdit}
                          data-testid={suffixTestId('materialDiscount.switch', props)}
                        />
                      </Tooltip>
                      <label htmlFor={materialDiscountSwitchId}>
                        <Heading size={4}>
                          {i18n.t('entity.warehouse.labels.discountOnMaterial')}
                        </Heading>
                      </label>
                    </HStack>
                    <Show when={isDiscountOnMaterialOpen}>
                      <VStack spacing={4}>
                        <FormField
                          control={control}
                          name="materialDiscount.discountType"
                          type="radio"
                          options={materialDiscountOptions}
                          data-testid={suffixTestId('materialDiscount.discountType', props)}
                        />
                        <Box width={PRICING_FIELD_WIDTH} maxWidth="100%">
                          <FormField
                            control={control}
                            name="materialDiscount.discountValue"
                            type="number"
                            label={i18n.t('entity.warehouse.labels.percentage')}
                            suffix="%"
                            isDisabled={props.isEditingDisabled || !directSaleEdit}
                            tooltip={
                              !directSaleEdit
                                ? i18n.t('module.warehouse.label.missingDirectSaleEditPermission')
                                : undefined
                            }
                            data-testid={suffixTestId('materialDiscount.discountValue', props)}
                          />
                        </Box>
                      </VStack>
                    </Show>
                  </VStack>
                </Card>
                <Align right>
                  <Tooltip
                    isDisabled={directSaleEdit}
                    label={i18n.t('module.warehouse.label.missingDirectSaleEditPermission')}
                  >
                    <FormButton
                      isDisabled={
                        isNotNil(customerContract) ||
                        isCustomDiscountDisabled ||
                        props.isEditingDisabled ||
                        !directSaleEdit
                      }
                      control={control}
                      type="submit"
                      title={i18n.t('general.actions.saveChanges')}
                      data-testid={suffixTestId('saveChanges', props)}
                    />
                  </Tooltip>
                </Align>
              </VStack>
            );
          }}
        </Form>
      </VStack>
    </DataStatus>
  );
}
